import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";
import {
  Receipt, DollarSign, Clock, CheckCircle2, XCircle, Building2, Megaphone, AlertTriangle,
} from "lucide-react";

const COLORS = ["#047857", "#B4881F", "#0F766E", "#65A30D", "#CA8A04", "#0891B2", "#D97706"];

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#1C2541] p-5">
      <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${accent}`}><Icon className="h-5 w-5" /></div>
      <div className="mt-4 text-2xl font-bold">{value}</div>
      <div className="text-xs text-emerald-100/50">{label}</div>
    </div>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <p className="text-emerald-100/50">Loading dashboard…</p>;

  return (
    <div>
      <h1 className="font-display text-3xl font-bold">Executive Dashboard</h1>
      <p className="mt-1 text-sm text-emerald-100/50">Overview of transactions, programs, partners and compliance.</p>

      <div className="mt-7 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="admin-kpis">
        <Kpi icon={Receipt} label="Total Transactions" value={d.total_transactions} accent="bg-emerald-800 text-white" />
        <Kpi icon={DollarSign} label="Total Amount (completed)" value={d.total_amount.toLocaleString()} accent="bg-gold/20 text-gold-light" />
        <Kpi icon={CheckCircle2} label="Successful Payments" value={d.successful_payments} accent="bg-emerald-700 text-white" />
        <Kpi icon={Clock} label="Pending Payments" value={d.pending_payments} accent="bg-cyan-800 text-white" />
        <Kpi icon={XCircle} label="Failed Payments" value={d.failed_payments} accent="bg-red-900 text-white" />
        <Kpi icon={Megaphone} label="Active Programs" value={d.active_programs} accent="bg-emerald-800 text-white" />
        <Kpi icon={Building2} label="Partner Organisations" value={d.partner_organisations} accent="bg-indigo-900 text-white" />
        <Kpi icon={Receipt} label="Refunds / Adjustments" value={d.refunds} accent="bg-amber-900 text-white" />
      </div>

      {d.compliance_alerts?.length > 0 && (
        <div className="mt-6 rounded-2xl border border-gold/30 bg-gold/10 p-5">
          <div className="flex items-center gap-2 font-semibold text-gold-light"><AlertTriangle className="h-4 w-4" /> Compliance Alerts</div>
          <ul className="mt-2 space-y-1 text-sm text-emerald-100/70">
            {d.compliance_alerts.map((c, i) => <li key={i}>• {c} — fundraising campaign requires compliance completion before publishing.</li>)}
          </ul>
        </div>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="rounded-2xl border border-white/10 bg-[#1C2541] p-5 lg:col-span-2">
          <h3 className="font-semibold">Transactions over time</h3>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={d.series}>
                <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#B4881F" stopOpacity={0.5} /><stop offset="100%" stopColor="#B4881F" stopOpacity={0} /></linearGradient></defs>
                <XAxis dataKey="date" stroke="#64748b" fontSize={11} />
                <YAxis stroke="#64748b" fontSize={11} />
                <Tooltip contentStyle={{ background: "#0B132B", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} />
                <Area type="monotone" dataKey="amount" stroke="#B4881F" strokeWidth={2} fill="url(#g)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-[#1C2541] p-5">
          <h3 className="font-semibold">Programs by category</h3>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={d.by_category} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={3}>
                  {d.by_category.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0B132B", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-white/10 bg-[#1C2541] p-5">
        <h3 className="font-semibold">Recent Transactions</h3>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-white/10 text-left text-xs uppercase text-emerald-100/40">
              <th className="pb-2">Reference</th><th className="pb-2">Program</th><th className="pb-2">Amount</th><th className="pb-2">Status</th><th className="pb-2">Date</th>
            </tr></thead>
            <tbody>
              {d.recent_transactions.map((t) => (
                <tr key={t.id} className="border-b border-white/5">
                  <td className="py-2.5 font-mono text-xs">{t.transaction_ref}</td>
                  <td className="py-2.5">{t.campaign_title}</td>
                  <td className="py-2.5">{t.currency} {t.amount?.toLocaleString()}</td>
                  <td className="py-2.5"><span className={`rounded-full px-2 py-0.5 text-xs ${t.status === "completed" ? "bg-emerald-800" : t.status === "failed" ? "bg-red-900" : "bg-amber-900"}`}>{t.status}</span></td>
                  <td className="py-2.5 text-emerald-100/50">{t.created_at?.slice(0, 10)}</td>
                </tr>
              ))}
              {d.recent_transactions.length === 0 && <tr><td colSpan="5" className="py-6 text-center text-emerald-100/40">No transactions yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
